/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * boardModel — the leaderboard's rules, with no network and no React, so each
 * one can be tested on its own.
 *
 * The rules that matter are the ones that would otherwise ship as bugs:
 * sorting never crosses a tab, the default sort follows the kind of race, and
 * the tabs that have nothing to sort do not offer a sort.
 */

/** Statuses, grouped into the tabs the board shows. */
export const TABS = [
  { key: 'completed', label: 'Completed', statuses: ['FINISHED'] },
  { key: 'in_progress', label: 'In progress', statuses: ['IN_PROGRESS'] },
  // DID_NOT_FINISH needs its own tab: somebody who started and pulled out at
  // 15 km did not "not participate". OTHER shares it.
  { key: 'did_not_finish', label: 'Did not finish', statuses: ['DID_NOT_FINISH', 'OTHER'] },
  { key: 'not_participated', label: 'Not participated', statuses: ['NOT_STARTED'] },
  { key: 'injured', label: 'Injured', statuses: ['INJURED'] },
];

export const DEFAULT_TAB = 'completed';

/** Neither distance nor time is recorded on these, so a sort would do nothing. */
const UNSORTABLE_TABS = new Set(['not_participated', 'injured']);

/** The tab a status belongs to, or null if the status is unknown. */
export function tabForStatus(status) {
  const code = String(status || '').toUpperCase();
  const tab = TABS.find((t) => t.statuses.includes(code));
  return tab ? tab.key : null;
}

/**
 * The tabs to draw. In progress is hidden while its count is zero: the app only
 * uploads a run after it finishes, so that tab is empty at every race today, and
 * a permanently empty tab reads as a fault. It reappears on its own if we ever
 * have live data.
 */
export function visibleTabs(counts) {
  const by = counts || {};
  return TABS.filter((tab) => tab.key !== 'in_progress' || Number(by[tab.key] || 0) > 0);
}

/** Whether the Distance and Time headings offer a sort on this tab. */
export function sortingAllowedOn(tabKey) {
  return !UNSORTABLE_TABS.has(tabKey);
}

/**
 * The sort a race opens on.
 *
 * Time ascending for a race with a set distance — that is the real ranking.
 * Distance descending ONLY for an open challenge ("run as far as you can in an
 * hour"), where time is the constant and distance is the achievement.
 *
 * Distance descending is wrong as a default for a fixed distance: in a 10K the
 * recorded figures come back 9.98, 10.04, 10.12, and that spread is GPS error,
 * so it would rank people by whose phone drifted most.
 */
export function defaultSortFor(race) {
  const row = race || {};
  const hasSetDistance = Number(row.total_distance) > 0 || row.distance_known === true;
  return hasSetDistance
    ? { column: 'duration_seconds', direction: 'asc' }
    : { column: 'distance_meters', direction: 'desc' };
}

/** Which way a column sorts when the viewer first clicks its heading. */
export function initialDirectionFor(column) {
  return column === 'distance_meters' ? 'desc' : 'asc';
}

/** What the board may show, decided by where the result was timed. */
export function displayRulesFor(timingSourceCode) {
  const code = String(timingSourceCode || '').toUpperCase();
  if (code === 'UNTIMED') {
    // No blank where a number would sit — a participation badge instead.
    return { time: false, distance: false, position: false, splits: false, pace: false };
  }
  if (code === 'ORGANIZER_IMPORT') {
    return { time: true, distance: false, position: true, splits: false, pace: false };
  }
  return { time: true, distance: true, position: true, splits: true, pace: true };
}

const EMPTY_COPY = {
  completed: ['No finishers yet', 'Results appear here as runs are added to the race.'],
  in_progress: ['Nobody is out on course',
    'A run only reaches us once the athlete finishes and their phone syncs, so this tab fills up as people cross the line.'],
  did_not_finish: ['No withdrawals recorded', 'Everyone who started has finished.'],
  not_participated: ['Every registered athlete started', 'Nobody on the entry list missed the race.'],
  injured: ['No injuries recorded', 'Nothing has been flagged by the medical team.'],
};

/** Title and sentence for an empty tab. */
export function emptyCopyFor(tabKey) {
  const [title, body] = EMPTY_COPY[tabKey] || EMPTY_COPY.completed;
  return { title, body };
}

/** "1:28:14", "34:11", or a dash when there is no time to show. */
export function formatClock(seconds) {
  const n = Math.round(Number(seconds));
  if (!Number.isFinite(n) || n <= 0) return '—';
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  const pad = (v) => String(v).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** "21.10 km", or a dash for an activity with no distance, such as yoga. */
export function formatKm(metres) {
  const n = Number(metres);
  return Number.isFinite(n) && n > 0 ? `${(n / 1000).toFixed(2)} km` : '—';
}

/** "4:16" per kilometre, or a dash when either figure is missing. */
export function formatPace(seconds, metres) {
  const t = Number(seconds);
  const d = Number(metres);
  if (!Number.isFinite(t) || !Number.isFinite(d) || t <= 0 || d <= 0) return '—';
  const perKm = Math.round(t / (d / 1000));
  return `${Math.floor(perKm / 60)}:${String(perKm % 60).padStart(2, '0')}`;
}

/** The 1-based page a position falls on, for "you are on page 2". */
export function pageOfPosition(position, pageSize) {
  const p = Number(position);
  const size = Number(pageSize);
  if (!Number.isFinite(p) || !Number.isFinite(size) || p < 1 || size < 1) return null;
  return Math.floor((p - 1) / size) + 1;
}

/**
 * "ahead of 88% of the field" — position and total are both already on the page.
 *
 * MEASURED AGAINST THE OTHERS, NOT AGAINST EVERYONE. The first cut divided by
 * the whole field, which counts the reader themselves: the winner of a race of
 * four had beaten 3 of 4 and was told "ahead of 75%", and nobody could ever
 * reach 100% because their own place was always in the denominator.
 *
 * It went unnoticed because it only shows up in a SMALL field — at 1,142
 * finishers the winner's 1141/1142 rounds to 100 and looks right.
 *
 * The honest question is how many of the OTHER finishers you came ahead of, so
 * the denominator is one less. A lone finisher has nobody to be ahead of and
 * gets null rather than a made-up number.
 */
export function percentBehind(position, total) {
  const p = Number(position);
  const n = Number(total);
  if (!Number.isFinite(p) || !Number.isFinite(n) || p < 1 || n < 1 || p > n) return null;
  if (n === 1) return null;
  return Math.round(((n - p) / (n - 1)) * 100);
}

/**
 * Sorts rows for the board.
 *
 * Done in the browser because the public read endpoint builds its SQL without
 * an ORDER BY. That loses the database's NULLS LAST, so empty values are sunk
 * explicitly here instead: yoga has no distance and a person who never started
 * has no time, and either would otherwise float to the top of an ascending
 * sort and read as the winner.
 *
 * The sort never crosses a tab — callers pass one tab's rows — which is what
 * keeps the ranking honest: somebody who stopped at 2 km cannot appear above a
 * finisher, because they are not in this list at all.
 */
export function sortRows(rows, column, direction) {
  const dir = direction === 'desc' ? -1 : 1;
  const value = (row) => {
    const n = Number((row || {})[column]);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  return [...(rows || [])].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    // Blanks last, whichever way the column is pointing.
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    if (x === y) return 0;
    return (x < y ? -1 : 1) * dir;
  });
}

/** The narrowest band the saved query groups by, in seconds. */
export const BAND_SECONDS = 300;

/** Below this many finishers a histogram is noise, not a shape. */
export const HISTOGRAM_MIN_FINISHERS = 3;

/** More bars than this stop being readable on a phone. */
const MAX_BARS = 32;

/**
 * Turns the saved query's bands into the bars the board draws.
 *
 * Three things the raw rows cannot do on their own:
 *
 * EMPTY BANDS ARE PUT BACK. The query groups, so a five-minute window nobody
 * finished in simply has no row. Drawing only the rows it returns would pack
 * the bars together and invent a smooth field out of a broken one — the gap
 * between a lead group and everybody else is exactly the shape worth seeing.
 *
 * WIDE FIELDS ARE MERGED, NOT TRUNCATED. A marathon can spread six hours, and
 * 72 slivers read as noise. Adjacent bands are added together until the count
 * fits, which widens the bucket honestly; dropping the tail would hide the
 * slowest finishers, who are no less part of the field.
 *
 * ONE BAR CAN BE THE READER'S. Passing their finish time marks the bar they
 * are standing in, which is the whole reason an athlete looks at this.
 *
 * @param {Array} bands    rows of { band_start_seconds, runners }
 * @param {Object} options { mineSeconds } the viewer's own finish time
 * @returns {Array} bars of { startSeconds, endSeconds, runners, share, mine }
 */
export function histogramBars(bands, { mineSeconds } = {}) {
  const rows = (bands || [])
    .map((b) => ({
      start: Math.floor(Number(b.band_start_seconds) / BAND_SECONDS),
      runners: Number(b.runners) || 0,
    }))
    .filter((b) => Number.isFinite(b.start) && b.runners > 0)
    .sort((a, b) => a.start - b.start);

  if (rows.length === 0) return [];

  const first = rows[0].start;
  const last = rows[rows.length - 1].start;
  // How many of the narrow bands go into one drawn bar.
  const merge = Math.max(1, Math.ceil((last - first + 1) / MAX_BARS));
  const barCount = Math.floor((last - first) / merge) + 1;

  const tally = new Array(barCount).fill(0);
  rows.forEach((b) => { tally[Math.floor((b.start - first) / merge)] += b.runners; });

  const tallest = Math.max(...tally);
  const mineBar = Number.isFinite(Number(mineSeconds)) && Number(mineSeconds) > 0
    ? Math.floor((Math.floor(Number(mineSeconds) / BAND_SECONDS) - first) / merge)
    : null;

  return tally.map((runners, i) => ({
    startSeconds: (first + i * merge) * BAND_SECONDS,
    endSeconds: (first + (i + 1) * merge) * BAND_SECONDS,
    runners,
    // Height is relative to the busiest bar, so the shape survives any field size.
    share: tallest > 0 ? runners / tallest : 0,
    mine: mineBar === i,
  }));
}
